import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Select, { components as SelectComponents } from 'react-select';
import { FiZap, FiAlertTriangle, FiX, FiCheck, FiInfo } from 'react-icons/fi';
import PulseLoader from 'react-spinners/PulseLoader';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { addNotification, notificationStates } from '@/components/Notification';
import { useProductivityApps } from './useProductivityApps';
import styles from './Profile.module.scss';

// ─── Custom Option ─────────────────────────────────────────────────────────────

const OptionWithCheck = (props) => (
  <SelectComponents.Option {...props}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <span>{props.label}</span>
      {props.isSelected && <FiCheck size={13} />}
    </div>
  </SelectComponents.Option>
);

const ValueContainer = ({ children, getValue, selectProps, ...props }) => {
  const selected = getValue();
  const showCount =
    selected.length > 0 && !selectProps.menuIsOpen && !selectProps.inputValue;
  return (
    <SelectComponents.ValueContainer
      getValue={getValue}
      selectProps={selectProps}
      {...props}
    >
      {showCount && (
        <span style={{ fontSize: '13px', color: '#374151' }}>
          {selected.length} {selected.length === 1 ? 'app' : 'apps'} selected
        </span>
      )}
      {children}
    </SelectComponents.ValueContainer>
  );
};

const SELECT_COMPONENTS = {
  Option: OptionWithCheck,
  MultiValue: () => null,
  ValueContainer,
};

// ─── Per-column react-select styles ───────────────────────────────────────────

const buildSelectStyles = (accentColor) => ({
  container: (base) => ({ ...base, width: '100%' }),
  control: (base, state) => ({
    ...base,
    border: `1.5px solid ${state.isFocused ? accentColor : '#d1d5db'}`,
    borderRadius: '8px',
    boxShadow: state.isFocused ? `0 0 0 3px ${accentColor}22` : 'none',
    minHeight: '38px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': { borderColor: accentColor },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? `${accentColor}18`
      : state.isFocused
        ? '#f3f4f6'
        : 'white',
    color: state.isSelected ? accentColor : '#111827',
    fontWeight: state.isSelected ? 500 : 400,
    fontSize: '13px',
    cursor: 'pointer',
    '&:active': { backgroundColor: `${accentColor}28` },
  }),
  placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9ca3af' }),
  input: (base) => ({ ...base, fontSize: '13px' }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    border: '1px solid #e0e4ea',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

const FOCUS_STYLES = buildSelectStyles('#22c55e');
const DISTRACTION_STYLES = buildSelectStyles('#ef4444');

// ─── AppColumn ────────────────────────────────────────────────────────────────

const AppColumn = ({
  title,
  icon,
  description,
  chipClass,
  apps,
  otherApps,
  knownAppNames,
  onChangeApps,
}) => {
  const isFocus = chipClass === styles.appChip_focus;

  const options = useMemo(
    () =>
      knownAppNames
        .filter(
          (name) =>
            !otherApps.some((a) => a.toLowerCase() === name.toLowerCase())
        )
        .map((name) => ({ value: name, label: name })),
    [knownAppNames, otherApps]
  );

  const value = apps.map((app) => ({ value: app, label: app }));

  return (
    <div className={styles.appListCard}>
      <div
        className={styles.appListCardAccent}
        data-variant={isFocus ? 'focus' : 'distraction'}
      />

      <div className={styles.appListCardHeader}>
        {icon}
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="primary-500"
        >
          {title}
        </Text>
      </div>

      <p className={styles.appListCardDescription}>{description}</p>

      <div className={styles.appListCardBody}>
        <Select
          isMulti
          options={options}
          value={value}
          onChange={(selected) =>
            onChangeApps((selected ?? []).map((s) => s.value))
          }
          components={SELECT_COMPONENTS}
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          backspaceRemovesValue={false}
          placeholder="Search and select apps…"
          menuPortalTarget={
            typeof window !== 'undefined' ? document.body : null
          }
          menuPosition="fixed"
          styles={isFocus ? FOCUS_STYLES : DISTRACTION_STYLES}
        />

        <div className={styles.appChips}>
          {apps.length === 0 ? (
            <span className={styles.appChipsEmpty}>Not yet selected.</span>
          ) : (
            apps.map((app) => (
              <span key={app} className={`${styles.appChip} ${chipClass}`}>
                {app}
                <button
                  className={styles.appChipRemove}
                  onClick={() => onChangeApps(apps.filter((a) => a !== app))}
                  title={`Remove ${app}`}
                >
                  <FiX size={11} />
                </button>
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ProductivityAppsTab ──────────────────────────────────────────────────────

const COMPANY_OPTION = { value: null, label: 'Company Defaults' };

export const ProductivityAppsTab = () => {
  const companyId = useSelector(
    (s) => s.auth.profile?.activeCompany?.companyId
  );
  const teamsData = useSelector((s) => s.team?.teams?.data ?? []);

  const contextOptions = useMemo(() => {
    const teamOptions = teamsData
      .filter((t) => !t.teamRemoved)
      .map((t) => ({ value: t.activeTeamid, label: t.Team }));
    return [COMPANY_OPTION, ...teamOptions];
  }, [teamsData]);

  const [selectedContext, setSelectedContext] = useState(COMPANY_OPTION);
  const selectedTeamId = selectedContext?.value ?? null;
  const isTeamContext = selectedTeamId != null;

  const {
    isLoading,
    isLoadingTeam,
    isSaving,
    hasChanges,
    knownAppNames,
    focusApps,
    distractionApps,
    setFocusApps,
    setDistractionApps,
    teamHasOverride,
    save,
    discardChanges,
    resetTeamOverride,
  } = useProductivityApps(companyId, selectedTeamId);

  const handleSave = async () => {
    const res = await save();
    if (res.success) {
      addNotification({
        message: 'Productivity apps saved.',
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: res.error || 'Failed to save. Please try again.',
        level: notificationStates.error,
      });
    }
  };

  const handleReset = async () => {
    const res = await resetTeamOverride();
    if (res.success) {
      addNotification({
        message: `${selectedContext.label} reset to company defaults.`,
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: res.error || 'Failed to reset. Please try again.',
        level: notificationStates.error,
      });
    }
  };

  return (
    <div className={styles.productivityAppsTab}>
      <div className={styles.profileTitleContainer}>
        <Text
          variant={textVariantsEnum.Text_xl}
          fontWeight={fontWeightEnum.Bold}
          color="primary-500"
        >
          Productivity Apps
        </Text>
      </div>

      {isLoading ? (
        <div className={styles.productivityAppsLoading}>
          <PulseLoader color="#3363f5" size={8} />
        </div>
      ) : (
        <div className={styles.productivityAppsBody}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-500"
          >
            Define which apps count as focused work and which are distractions.
          </Text>

          {/* Context switcher — locked while there are unsaved changes */}
          <div className={styles.productivityAppsContext}>
            <span className={styles.productivityAppsContextLabel}>
              Editing for
            </span>
            <Dropdown
              options={contextOptions}
              value={selectedContext}
              onChange={setSelectedContext}
              width="240px"
              isDisabled={hasChanges}
              selectStyles={{
                control: { height: '34px' },
                valueContainer: { height: '34px' },
              }}
            />
            {hasChanges && (
              <span className={styles.productivityAppsContextHint}>
                Save or discard changes first
              </span>
            )}
          </div>

          {/* Team has no override — info hint */}
          {isTeamContext && !isLoadingTeam && !teamHasOverride && (
            <div className={styles.productivityAppsHint}>
              <FiInfo size={14} />
              <span>
                This team has no custom list. Company defaults are used silently
                for calculations. Add apps below and save to create a
                team-specific override.
              </span>
            </div>
          )}

          {isLoadingTeam ? (
            <div className={styles.productivityAppsLoading}>
              <PulseLoader color="#3363f5" size={8} />
            </div>
          ) : (
            <>
              <div className={styles.productivityAppsColumns}>
                <AppColumn
                  title="Focus Apps"
                  icon={<FiZap size={18} color="#22c55e" />}
                  description="Apps that indicate productive, focused work."
                  chipClass={styles.appChip_focus}
                  apps={focusApps}
                  otherApps={distractionApps}
                  knownAppNames={knownAppNames}
                  onChangeApps={setFocusApps}
                />
                <AppColumn
                  title="Distraction Apps"
                  icon={<FiAlertTriangle size={18} color="#ef4444" />}
                  description="Apps that typically distract from work."
                  chipClass={styles.appChip_distraction}
                  apps={distractionApps}
                  otherApps={focusApps}
                  knownAppNames={knownAppNames}
                  onChangeApps={setDistractionApps}
                />
              </div>

              <div className={styles.productivityAppsActions}>
                {isTeamContext && teamHasOverride && (
                  <button
                    className={styles.resetLink}
                    onClick={handleReset}
                    disabled={isSaving}
                  >
                    Reset to company defaults
                  </button>
                )}
                {hasChanges && (
                  <Button
                    text="Discard"
                    variant={buttonVariants.secondary}
                    disabled={isSaving}
                    onClick={discardChanges}
                  />
                )}
                <Button
                  text={isSaving ? 'Saving…' : 'Save Changes'}
                  variant={buttonVariants.primary}
                  disabled={!hasChanges || isSaving}
                  onClick={handleSave}
                  style={{
                    backgroundColor: hasChanges ? '#3363F5' : undefined,
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
